<template>
  <section class="py-[42px] lg:py-21">
    <section ref="statsSection" class="grid gap-4" :class="calculatedColums">
      <template
        v-for="({ statistic, description }, index) in props.statistics"
        :key="`${description}-${index}`"
      >
        <div
          class="min-h-40 grid col-span-full md:col-span-1 grid-cols-1 grid-rows-2 gap-[14px] place-content-center place-items-center p-5"
          :class="[
            /* Mobile (1 col): alternate by row (index = row) */
            index % 2 === 0 ? 'bg-mbzuai-sand-500' : 'bg-mbzuai-sand-300',
          ]"
        >
          <div class="flex flex-row gap-0">
            <h6
              :ref="(el) => setStatRef(el as HTMLElement, index)"
              class="text-h6 text-[50px] font-extrabold text-mbzuai-navy-500!"
            >
              {{
                hasAnimated
                  ? statistic
                  : parseStat(statistic).prefix +
                    '0' +
                    parseStat(statistic).suffix
              }}
            </h6>
          </div>
          <p
            class="text-caption text-center row-start-2 text-mbzuai-dark-navy-500!"
          >
            {{ description }}
          </p>
        </div>
      </template>
    </section>
  </section>
</template>

<script setup lang="ts">
import { gsap } from 'gsap';
import type { StatisticsStatsBlockCon } from '~/types';

const props = defineProps<{
  statistics: StatisticsStatsBlockCon[];
}>();

const statsSection = ref<HTMLElement | null>(null);
const statEls = ref<(HTMLElement | null)[]>([]);
const hasAnimated = ref(false);
const statsTotal = props.statistics.length;

const setStatRef = (el: HTMLElement | null, index: number) => {
  statEls.value[index] = el;
};

const calculatedColums = computed(() => {
  switch (statsTotal) {
    case 2:
      return 'md:grid-cols-2';
    case 3:
      return 'md:grid-cols-3';
    case 4:
      return 'md:grid-cols-2 lg:grid-cols-4';
    case 5:
      return 'md:grid-cols-2 lg:grid-cols-5';
    case 6:
      return 'md:grid-cols-2 lg:grid-cols-6';
    default:
      return 'grid-cols-1';
  }
});

/** Extract prefix (e.g. "$"), numeric value, and suffix (e.g. "+", "M", "%") from a statistic string. */
const parseStat = (stat: string) => {
  const match = stat.match(/^([^\d]*)([\d,.]+)(.*)$/);
  if (!match) return { prefix: '', number: 0, suffix: stat };
  return {
    prefix: match[1],
    number: parseFloat(match[2]!.replace(/,/g, '')),
    suffix: match[3],
  };
};

onMounted(() => {
  if (!statsSection.value) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !hasAnimated.value) {
          hasAnimated.value = true;
          animateCountUp();
          observer.disconnect();
        }
      });
    },
    { threshold: 0.5 }
  );

  observer.observe(statsSection.value);
});

const animateCountUp = () => {
  props.statistics.forEach(({ statistic }, index) => {
    const el = statEls.value[index];
    if (!el) return;

    const { prefix, number, suffix } = parseStat(statistic);
    const isDecimal = statistic.includes('.') && /\.\d+/.test(statistic);
    const decimalPlaces = isDecimal
      ? (statistic.match(/\.(\d+)/)?.[1]?.length ?? 0)
      : 0;
    const counter = { value: 0 };
    gsap.to(counter, {
      value: number,
      duration: 2,
      delay: index * 0.15,
      ease: 'power2.out',
      onUpdate: () => {
        let formatted =
          decimalPlaces > 0
            ? counter.value.toFixed(decimalPlaces)
            : Math.round(counter.value).toString();

        const parts = formatted.split('.');
        if (parts[0]) {
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        formatted = parts.join('.');

        el.textContent = `${prefix}${formatted}${suffix}`;
      },
    });
  });
};
</script>
